import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Calendar as CalendarIcon, ChevronLeft, ChevronRight, Compass, Droplets, Flower2, Gift, Heart, Instagram, Leaf, MapPin, Mountain, ShieldCheck, Sparkles, Star, Trees, Users, Waves } from "lucide-react";
import { api, formatINR } from "@/lib/api";
import { HERO, HOME, STAYS } from "@/constants/testIds";
import { toast } from "sonner";

import PropertyCard from "@/components/PropertyCard";
import SectionHeading from "@/components/SectionHeading";

const ICONS = {
  Trees, Droplets, Waves, Mountain, Users, Heart, Compass, Flower2,
};

const EXPERIENCE_COPY = {
  forest_retreats:     "Glass cubes in pepper plantations and cabins lost in old-growth canopy.",
  private_pool_villas: "Heated infinity pools, private chefs and zero shared walls.",
  riverside_escapes:   "Decks that hang over rapids; mornings with running water.",
  mountain_view_stays: "Tea estates above the clouds and cliff-edge cantilever homes.",
  family_getaways:     "Big homes, big tables, lawn games and bonfires for fifteen.",
  romantic_retreats:   "A-frames built for two, copper tubs and curated vinyl libraries.",
  adventure_escapes:   "Dune camps, treehouses and trekkable backyards.",
  wellness_retreats:   "Live-in yoga teachers, Ayurvedic kitchens and sound baths.",
};

const HERO_VIDEO = "https://customer-assets-m6fa6gv7.emergentagent.net/job_jungle-retreat-3/artifacts/dfcq3jk9_Tarzan%20Home%20page.mp4";
const HERO_POSTER = "https://customer-assets.emergentagent.com/job_jungle-retreat-3/artifacts/94d2njbn_Hero.png";
const TESTI_BG = "https://images.unsplash.com/photo-1486707471592-8e7eb7e36f78?auto=format&fit=crop&w=2000&q=80";
const NEWSLETTER_BG = "https://images.unsplash.com/photo-1491497895121-1334fc14d8c9?auto=format&fit=crop&w=2000&q=80";
const BENTO = [
  // Tarzan Villa collection - client Drive photos
  "https://lh3.googleusercontent.com/d/1vZVBrym_Gjxc_Jh2XZWDuG4ilkezTaEM=w1600",
  "https://lh3.googleusercontent.com/d/1pECItp5h_wGorLAAE8ke1_JpILbqRIW3=w1200",
  "https://lh3.googleusercontent.com/d/1Jz2xHq127g2LtC7s-dObbx5W4JkGZhKN=w1200",
  "https://lh3.googleusercontent.com/d/1_TJKPoQ5YWnOt8L4S4SFqUkvcGzdsTeT=w1200",
];
const MOMENTS = [
  // Diverse mix from client Drive — Hyderabad property photos + one shot per collection
  "https://lh3.googleusercontent.com/d/1JASTJqPCY0RbtIztaCx8eRP4_8tnl8nF=w1400", // Hyd pool hero (feature tile)
  "https://lh3.googleusercontent.com/d/1qGgy9kngs99pFbUGcZJo4jRfpiQ87aBb=w900",  // Hyd interior 1
  "https://lh3.googleusercontent.com/d/1APIY5ZfPfRAMPLTnmcRJt2hZPFl1lK22=w900",  // Hyd interior 2
  "https://lh3.googleusercontent.com/d/1o8zdrsUfIm-SiNlsfOtVfHW8nGdRclz4=w900",  // Hyd interior 3
  "https://lh3.googleusercontent.com/d/1hubFWXDNIYIGRc3SKtelOzpAZPYIu0ln=w900",  // Hyd interior 4
  "https://lh3.googleusercontent.com/d/1CNGBrLR0epVIEKOsip0DeK48Ee7cWI31=w900",  // Hyd interior 5
  "https://lh3.googleusercontent.com/d/1YgomR_JGWqXNxpJd9LNrfYjMnvnCRSUj=w900",  // Hyd interior 6
];

const MARQUEE = [
  // Community
  "https://lh3.googleusercontent.com/d/15o0aiHe3XO-R-Qe-QZMrIB1WlxTBD_9A=w900",
  "https://lh3.googleusercontent.com/d/1W5bd9I-peA1ZyOsk1ZQEk52cRJcNM2rL=w900",
  "https://lh3.googleusercontent.com/d/1mxRCSg25BWwOqoclwHL2ufP45I94FS7s=w900",
  "https://lh3.googleusercontent.com/d/1sxH9v-b_Tc0HB1MZ1MUYgf3_zIyR7h45=w900",
  // Farmstay
  "https://lh3.googleusercontent.com/d/1aqwimeRWVRvBrO0K0z2B3jYJradfoyMY=w900",
  "https://lh3.googleusercontent.com/d/1ELduEHdzKI4fe8eayE0Kc3hlymJyHtBc=w900",
  "https://lh3.googleusercontent.com/d/1v2FrLk41BCN-LBOiCXs4cGC-pxcurD5S=w900",
  // Villa
  "https://lh3.googleusercontent.com/d/1vZVBrym_Gjxc_Jh2XZWDuG4ilkezTaEM=w900",
  "https://lh3.googleusercontent.com/d/1pECItp5h_wGorLAAE8ke1_JpILbqRIW3=w900",
  "https://lh3.googleusercontent.com/d/1Jz2xHq127g2LtC7s-dObbx5W4JkGZhKN=w900",
  "https://lh3.googleusercontent.com/d/1_TJKPoQ5YWnOt8L4S4SFqUkvcGzdsTeT=w900",
  // Village
  "https://lh3.googleusercontent.com/d/1JPd3BLPd_sm-G24ZAHMP3WcjcEDAs2yY=w900",
  "https://lh3.googleusercontent.com/d/1EO8v8xr3-llQ4QwwKVOSJZrNfrL1oq7p=w900",
  "https://lh3.googleusercontent.com/d/12X5y49BreUAjm0dH2ZIpajvR1wtwk_-A=w900",
  "https://lh3.googleusercontent.com/d/1sduH3nC8ZpYHxUhfMD7pa4Y5hUMsGBXj=w900",
];

export default function Home() {
  const nav = useNavigate();
  const [featured, setFeatured] = useState([]);
  const [destinations, setDestinations] = useState([]);
  const [experiences, setExperiences] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [tIdx, setTIdx] = useState(0);

  const [form, setForm] = useState({ destination: "Hyderabad", checkin: "", checkout: "", guests: "", experience: "" });
  const [email, setEmail] = useState("");

  useEffect(() => {
    Promise.all([
      api.get("/properties", { params: { featured: true, limit: 8 } }),
      api.get("/destinations"),
      api.get("/experiences"),
      api.get("/testimonials"),
    ]).then(([f, d, e, t]) => {
      setFeatured(f.data); setDestinations(d.data); setExperiences(e.data); setTestimonials(t.data);
    }).catch(() => {});
  }, []);

  const submitSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (form.destination) params.set("destination", form.destination);
    if (form.experience) params.set("experience", form.experience);
    nav(`/stays?${params.toString()}`);
  };

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    try {
      await api.post("/newsletter", { email });
      toast.success("You're on the list. Welcome to Tarzan.");
      setEmail("");
    } catch {
      toast.error("Could not subscribe. Please try again.");
    }
  };

  const t = testimonials[tIdx];

  return (
    <div data-testid="home-page">
      {/* ============ HERO ============ */}
      <section className="relative h-[100svh] min-h-[760px] w-full overflow-hidden">
        <video
          src={HERO_VIDEO}
          poster={HERO_POSTER}
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-forest-900/55 via-forest-900/25 to-forest-900/85" />
        <div className="absolute inset-0 bg-gradient-to-r from-forest-900/40 to-transparent" />

        <div className="relative h-full container-tarzan flex flex-col justify-end pb-40 md:pb-64">
          <div className="max-w-3xl animate-fade-up">
            <div className="text-ivory/85 text-[10px] md:text-sm tracking-[0.32em] uppercase font-medium mb-4 md:mb-6">Escape · Explore · Experience</div>
            <h1 className="font-serif text-ivory text-[44px] leading-[1.02] md:text-7xl lg:text-[88px] md:leading-[0.98] tracking-tight text-balance">
              Escape Into<br/><em className="not-italic text-sand-200">Nature's Luxury</em>
            </h1>
            <p className="text-ivory/85 text-sm md:text-lg max-w-xl mt-5 md:mt-7 leading-relaxed text-pretty">
              Handpicked retreats, glass forest villas, riverside cottages and unforgettable experiences — curated for couples, families and quiet explorers.
            </p>
          </div>
        </div>

        {/* Floating Search — compact on mobile, full grid on desktop */}
        <div className="absolute left-0 right-0 bottom-10 md:bottom-10 z-20 pointer-events-none">
          <div className="container-tarzan pointer-events-auto">
            {/* MOBILE: compact single-row (Destination + submit) */}
            <form
              onSubmit={submitSearch}
              className="md:hidden mx-3 bg-ivory rounded-full luxe-shadow border border-forest/10 animate-fade-up flex items-center gap-2 pl-4 pr-1 py-1"
              data-testid="hero-search-mobile"
            >
              <MapPin className="h-4 w-4 text-forest-400 shrink-0" />
              <input
                data-testid={HERO.searchDestination + "-mobile"}
                value={form.destination}
                onChange={(e)=>setForm({...form, destination:e.target.value})}
                placeholder="Where to?"
                className="flex-1 min-w-0 bg-transparent outline-none text-sm font-medium text-ink placeholder:text-ink-soft/60 py-3"
              />
              <button
                data-testid={HERO.searchSubmit + "-mobile"}
                type="submit"
                aria-label="Find Your Retreat"
                className="h-11 w-11 rounded-full bg-forest text-ivory grid place-items-center hover:bg-forest-700 transition-colors shrink-0"
              >
                <ArrowRight className="h-4 w-4" />
              </button>
            </form>

            {/* DESKTOP: full multi-field search */}
            <form onSubmit={submitSearch} className="hidden md:block bg-ivory backdrop-blur-xl rounded-full luxe-shadow border border-forest/10 animate-fade-up p-2">
              <div className="flex items-stretch">
                <SearchField icon={<MapPin className="h-4 w-4 text-forest-400" />} label="Destination" divider>
                  <input data-testid={HERO.searchDestination} value={form.destination} onChange={(e)=>setForm({...form, destination:e.target.value})} placeholder="Where to?" className="w-full bg-transparent outline-none text-sm font-medium text-ink placeholder:text-ink-soft/60" />
                </SearchField>
                <SearchField icon={<CalendarIcon className="h-4 w-4 text-forest-400" />} label="Check In" divider>
                  <input data-testid={HERO.searchCheckin} type="date" value={form.checkin} onChange={(e)=>setForm({...form, checkin:e.target.value})} className="w-full bg-transparent outline-none text-sm font-medium text-ink" />
                </SearchField>
                <SearchField icon={<CalendarIcon className="h-4 w-4 text-forest-400" />} label="Check Out" divider>
                  <input data-testid={HERO.searchCheckout} type="date" value={form.checkout} onChange={(e)=>setForm({...form, checkout:e.target.value})} className="w-full bg-transparent outline-none text-sm font-medium text-ink" />
                </SearchField>
                <SearchField icon={<Users className="h-4 w-4 text-forest-400" />} label="Guests" divider>
                  <input data-testid={HERO.searchGuests} value={form.guests} onChange={(e)=>setForm({...form, guests:e.target.value})} placeholder="Add guests" className="w-full bg-transparent outline-none text-sm font-medium text-ink placeholder:text-ink-soft/60" />
                </SearchField>
                <SearchField icon={<Leaf className="h-4 w-4 text-forest-400" />} label="Experience">
                  <select data-testid={HERO.searchExperience} value={form.experience} onChange={(e)=>setForm({...form, experience:e.target.value})} className="w-full bg-transparent outline-none text-sm font-medium text-ink -ml-1">
                    <option value="">Any</option>
                    {experiences.map(x => <option key={x.slug} value={x.slug}>{x.name}</option>)}
                  </select>
                </SearchField>
                <button data-testid={HERO.searchSubmit} type="submit" className="bg-forest text-ivory rounded-full px-7 my-1 mx-1 font-medium text-sm hover:bg-forest-700 transition-all flex items-center justify-center gap-2 whitespace-nowrap">
                  Find Your Retreat <ArrowRight className="h-4 w-4" />
                </button>
              </div>
            </form>
          </div>
        </div>
      </section>

      {/* ============ THE FOUR RETREAT COLLECTIONS ============ */}
      <section className="pt-20 md:pt-28 pb-8 bg-ivory" data-testid="home-categories">
        <div className="container-tarzan">
          <div className="max-w-2xl mb-12">
            <div className="eyebrow mb-3">The Four Collections</div>
            <h2 className="font-serif text-4xl md:text-5xl text-ink leading-[1.05] tracking-tight text-balance">Every Tarzan destination belongs to one of four families.</h2>
            <p className="text-ink-soft mt-4 leading-relaxed text-base md:text-lg">Pick the kind of retreat that fits the moment — a working farm, a private villa, a cluster of cabins or a community-built lodge.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              { slug: "community", name: "Tarzan Community", tag: "Neighbourhood-rooted retreats", img: "https://customer-assets-m6fa6gv7.emergentagent.net/job_jungle-retreat-3/artifacts/vvz0ntpo_Tarzan%20community.png" },
              { slug: "farmstay",  name: "Tarzan Farmstay",  tag: "Working farms opened up",       img: "https://customer-assets-m6fa6gv7.emergentagent.net/job_jungle-retreat-3/artifacts/eo59m82s_Tarzan%20farmstay.png" },
              { slug: "villa",     name: "Tarzan Villa",     tag: "Architecture-led private villas",img: "https://customer-assets-m6fa6gv7.emergentagent.net/job_jungle-retreat-3/artifacts/n4ztj8kz_Tarzan%20Villa.png" },
              { slug: "village",   name: "Tarzan Village",   tag: "Cabin hamlets for groups",      img: "https://customer-assets-m6fa6gv7.emergentagent.net/job_jungle-retreat-3/artifacts/bi3opk9t_Tarzan%20Village.png" },
            ].map(c => (
              <Link key={c.slug} to={`/retreats/${c.slug}`} data-testid={`home-cat-${c.slug}`} className="group relative aspect-[3/4] rounded-3xl overflow-hidden soft-shadow hover:luxe-shadow transition-all hover:-translate-y-1">
                <img src={c.img} alt={c.name} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-[1500ms]" loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-t from-forest-900/95 via-forest-900/30 to-transparent" />
                <div className="absolute inset-x-0 bottom-0 p-6 text-ivory">
                  <div className="font-serif text-2xl leading-tight">{c.name}</div>
                  <div className="text-xs text-ivory/75 mt-2">{c.tag}</div>
                  <div className="mt-4 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-sand-200 group-hover:gap-3 transition-all">Explore <ArrowRight className="h-3.5 w-3.5" /></div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ============ FEATURED RETREATS ============ */}
      <section className="pt-16 md:pt-20 pb-24" data-testid="home-featured">
        <div className="container-tarzan">
          <SectionHeading
            eyebrow="Featured Retreats"
            title="Handpicked stays for your perfect getaway"
            subtitle="A small, curated edit of the most-loved homes across our collection."
            action={<Link to="/stays" className="text-forest text-sm tracking-[0.18em] uppercase font-medium hover:gap-3 inline-flex items-center gap-2 transition-all">View All <ArrowRight className="h-4 w-4" /></Link>}
          />
          <div data-testid={STAYS.pageGrid} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-7">
            {featured.slice(0, 8).map((p, i) => <PropertyCard key={p.id} p={p} eager={i < 4} />)}
          </div>
        </div>
      </section>

      {/* ============ DISCOVER BY EXPERIENCE ============ */}
      <section className="py-20 bg-forest-50/60" data-testid="home-experiences">
        <div className="container-tarzan">
          <SectionHeading
            eyebrow="Discover By Experience"
            title="Choose your kind of escape"
            subtitle="From private-pool villas to riverside silence — what kind of slow do you need?"
          />
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-6">
            {experiences.map((x) => {
              const Icon = ICONS[x.icon] || Trees;
              return (
                <Link key={x.slug} to={`/stays?experience=${x.slug}`} className="group flex flex-col items-center text-center p-5 rounded-2xl hover:bg-white hover:soft-shadow transition-all">
                  <div className="h-16 w-16 rounded-full border border-forest/15 grid place-items-center bg-ivory group-hover:bg-forest group-hover:border-forest transition-colors">
                    <Icon className="h-7 w-7 text-forest group-hover:text-ivory transition-colors" strokeWidth={1.4} />
                  </div>
                  <div className="font-serif text-base text-ink mt-4 leading-tight">{x.name}</div>
                  <div className="text-xs text-ink-soft mt-1">{x.stays_count} stays</div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* ============ TRENDING DESTINATIONS ============ */}
      <section className="py-24" data-testid="home-destinations">
        <div className="container-tarzan">
          <SectionHeading
            eyebrow="Trending Destinations"
            title="Places that keep calling our guests back"
            action={<Link to="/destinations" className="text-forest text-sm tracking-[0.18em] uppercase font-medium hover:gap-3 inline-flex items-center gap-2 transition-all">View All <ArrowRight className="h-4 w-4" /></Link>}
          />
          <div className="flex gap-5 overflow-x-auto scroll-hide -mx-6 px-6 md:mx-0 md:px-0 md:grid md:grid-cols-3 lg:grid-cols-6">
            {destinations.slice(0, 6).map((d) => (
              <Link key={d.slug} to={`/stays?destination=${d.name}`} className="group relative shrink-0 w-[260px] md:w-auto aspect-[3/4] rounded-2xl overflow-hidden soft-shadow hover:luxe-shadow transition-all">
                <img src={d.image} alt={d.name} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-[1200ms]" />
                <div className="absolute inset-0 bg-gradient-to-t from-forest-900/90 via-forest-900/20 to-transparent" />
                <div className="absolute inset-x-0 bottom-0 p-5 text-ivory">
                  <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] text-ivory/80"><MapPin className="h-3 w-3" />{d.state}</div>
                  <div className="font-serif text-2xl mt-1">{d.name}</div>
                  <div className="text-xs text-ivory/75 mt-1">{d.stays_count} stays</div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ============ CURATED LUXURY VILLAS BENTO ============ */}
      <section className="py-24 bg-sand-50" data-testid="home-curated">
        <div className="container-tarzan grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-4">
            <div className="eyebrow mb-4">Luxury Living, Naturally</div>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-[56px] text-ink leading-[1.02] tracking-tight text-balance">
              Curated villas for<br/><em className="not-italic text-forest">every moment.</em>
            </h2>
            <p className="text-ink-soft mt-6 text-base md:text-lg leading-relaxed max-w-md text-pretty">
              From cozy cottages to sprawling villas, every Tarzan home is personally visited and approved — designed to connect you with nature without compromising on comfort.
            </p>
            <Link to="/stays" className="inline-flex items-center gap-2 mt-8 bg-forest text-ivory px-7 py-3.5 rounded-full text-sm font-medium hover:bg-forest-700 transition-all hover:-translate-y-0.5">
              Explore Stays <ArrowRight className="h-4 w-4" />
            </Link>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-5 mt-10 max-w-md">
              {[
                { i: Droplets, l: "Private Pool" },
                { i: Trees,    l: "Forest View" },
                { i: Sparkles, l: "Chef on Call" },
                { i: Leaf,     l: "Bonfire" },
                { i: ShieldCheck, l: "Secure WiFi" },
              ].map(({i:Ic,l}) => (
                <div key={l} className="flex items-center gap-2 text-sm text-ink">
                  <Ic className="h-4 w-4 text-forest-400" strokeWidth={1.5} /> <span>{l}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-8 grid grid-cols-6 grid-rows-2 gap-3 md:gap-4 h-[460px] md:h-[560px]">
            <div className="col-span-4 row-span-2 rounded-3xl overflow-hidden"><img src={BENTO[0]} className="w-full h-full object-cover hover:scale-105 transition-transform duration-[1200ms]" alt="" /></div>
            <div className="col-span-2 row-span-1 rounded-3xl overflow-hidden"><img src={BENTO[1]} className="w-full h-full object-cover hover:scale-105 transition-transform duration-[1200ms]" alt="" /></div>
            <div className="col-span-2 row-span-1 rounded-3xl overflow-hidden"><img src={BENTO[2]} className="w-full h-full object-cover hover:scale-105 transition-transform duration-[1200ms]" alt="" /></div>
          </div>
        </div>
      </section>

      {/* ============ WHY CHOOSE TARZAN + BECOME A HOST ============ */}
      <section className="py-24">
        <div className="container-tarzan grid grid-cols-1 lg:grid-cols-3 gap-10">
          <div className="lg:col-span-2">
            <SectionHeading eyebrow="Why Tarzan" title="The difference is in the details." />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mt-2">
              {[
                { i: ShieldCheck, h: "Handpicked Properties", b: "We personally visit and approve only the finest stays — no listings, ever." },
                { i: Leaf,        h: "Nature-Centric Locations", b: "Every home sits in the lap of nature, far from crowds and concrete." },
                { i: Users,       h: "Dedicated Hospitality", b: "Concierge with a personal touch — we know your name and your preferences." },
                { i: Sparkles,    h: "Curated Experiences", b: "Bonfires, plantation walks, private chefs and silent yoga, arranged for you." },
              ].map(({i:Ic,h,b}) => (
                <div key={h} className="bg-ivory border border-forest/10 rounded-2xl p-6 soft-shadow">
                  <div className="h-11 w-11 rounded-full bg-forest-50 grid place-items-center mb-4"><Ic className="h-5 w-5 text-forest" strokeWidth={1.5} /></div>
                  <div className="font-serif text-lg text-ink">{h}</div>
                  <div className="text-sm text-ink-soft mt-2 leading-relaxed">{b}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="relative rounded-3xl overflow-hidden bg-forest-900 text-ivory p-10 min-h-[400px] flex flex-col justify-end">
            <img src="https://images.pexels.com/photos/29550075/pexels-photo-29550075.jpeg?auto=compress&cs=tinysrgb&w=1200" alt="" className="absolute inset-0 w-full h-full object-cover opacity-50" />
            <div className="absolute inset-0 bg-gradient-to-t from-forest-900 via-forest-900/70 to-forest-900/10" />
            <div className="relative">
              <div className="eyebrow text-sand-200 mb-3">Host With Tarzan</div>
              <h3 className="font-serif text-3xl md:text-4xl">Own a beautiful home in the wild?</h3>
              <p className="text-ivory/75 text-sm mt-3 max-w-sm leading-relaxed">Partner with us and earn handsomely while we take care of guests, marketing and care.</p>
              <Link to="/become-a-host" className="inline-flex items-center gap-2 mt-6 bg-ivory text-forest px-6 py-3 rounded-full text-sm font-medium hover:bg-sand-100 transition-colors">
                Know More <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ============ TESTIMONIALS (DARK) — hidden per client request ============ */}
      {false && (
      <section className="relative py-32 overflow-hidden bg-forest-900 text-ivory" data-testid="home-testimonials">
        <img src={TESTI_BG} alt="" className="absolute inset-0 w-full h-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-b from-forest-900/85 via-forest-900/80 to-forest-900/95" />
        <div className="relative container-tarzan grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-4">
            <div className="eyebrow text-sand-200 mb-4">Guest Love</div>
            <h2 className="font-serif text-4xl md:text-5xl leading-[1.05] tracking-tight">Loved by guests,<br/><em className="not-italic text-sand-200">remembered always.</em></h2>
            <p className="text-ivory/70 mt-5 max-w-md leading-relaxed">Real stories from real guests who found their perfect escape with Tarzan.</p>
            <div className="flex items-center gap-3 mt-8">
              <div className="flex">
                {[0,1,2,3,4].map(i => <Star key={i} className="h-5 w-5 fill-ember stroke-ember" />)}
              </div>
              <span className="font-serif text-3xl num">4.8<span className="text-ivory/60 text-base">/5</span></span>
            </div>
            <div className="text-xs text-ivory/55 mt-1">From 2,850+ verified reviews</div>
            <div className="flex items-center gap-3 mt-8">
              <button onClick={()=>setTIdx((tIdx - 1 + testimonials.length) % testimonials.length)} className="h-11 w-11 rounded-full border border-ivory/25 grid place-items-center hover:bg-ivory hover:text-forest-900 transition-colors"><ChevronLeft className="h-4 w-4" /></button>
              <button onClick={()=>setTIdx((tIdx + 1) % testimonials.length)} className="h-11 w-11 rounded-full border border-ivory/25 grid place-items-center hover:bg-ivory hover:text-forest-900 transition-colors"><ChevronRight className="h-4 w-4" /></button>
            </div>
          </div>
          <div className="lg:col-span-8 grid grid-cols-1 md:grid-cols-3 gap-5">
            {testimonials.slice(0,3).map((tt, i) => (
              <div key={tt.id || i} className={`bg-ivory text-ink rounded-2xl p-6 ${i === 1 ? "md:mt-8" : ""}`}>
                <div className="font-serif text-2xl text-forest leading-none mb-3">"</div>
                <p className="text-sm leading-relaxed text-ink">{tt.quote}</p>
                <div className="flex items-center gap-3 mt-5 pt-5 border-t border-forest/10">
                  <img src={tt.avatar} alt={tt.name} className="h-10 w-10 rounded-full object-cover" />
                  <div className="min-w-0">
                    <div className="font-medium text-sm text-ink truncate">{tt.name}</div>
                    <div className="text-xs text-ink-soft truncate">{tt.stay_name}</div>
                  </div>
                </div>
                <div className="flex gap-1 mt-3">
                  {Array.from({length: tt.rating || 5}).map((_,j) => <Star key={j} className="h-3.5 w-3.5 fill-ember stroke-ember" />)}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      )}

      {/* ============ MOMENTS FROM TARZAN ============ */}
      <section className="py-24" data-testid="home-moments">
        <div className="container-tarzan">
          <div className="flex items-end justify-between mb-10">
            <div>
              <div className="eyebrow mb-3">Moments from Tarzan</div>
              <h2 className="font-serif text-3xl md:text-4xl text-ink">Postcards from our guests</h2>
            </div>
            <a href="https://www.instagram.com/tarzanretreat/" target="_blank" rel="noopener noreferrer" data-testid="home-follow-instagram" className="hidden md:inline-flex items-center gap-2 text-forest text-sm tracking-[0.18em] uppercase font-medium hover:gap-3 transition-all">
              <Instagram className="h-4 w-4" /> Follow us
            </a>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 md:gap-4">
            {MOMENTS.map((src, i) => (
              <div key={i} className={`relative aspect-square rounded-2xl overflow-hidden ${i === 0 ? "col-span-2 row-span-2 aspect-auto md:aspect-square" : ""}`}>
                <img src={src} alt="" className="w-full h-full object-cover hover:scale-110 transition-transform duration-[1500ms]" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ GIFT CARD ============ */}
      <section className="py-24 bg-ivory" data-testid="home-giftcard">
        <div className="container-tarzan grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Card visual */}
          <div className="order-2 lg:order-1 flex justify-center lg:justify-start">
            <div className="relative w-full max-w-xl aspect-[3/2] rounded-[28px] overflow-hidden luxe-shadow bg-forest-900">
              <img
                src="https://customer-assets.emergentagent.com/job_jungle-retreat-3/artifacts/olu5wd9r_ChatGPT%20Image%20Jun%202%2C%202026%2C%2002_38_42%20PM.png"
                alt="Tarzan e-gift card — An Unforgettable Experience"
                className="absolute inset-0 w-full h-full object-cover"
                loading="lazy"
              />
            </div>
          </div>

          {/* Content */}
          <div className="order-1 lg:order-2 max-w-lg">
            <div className="eyebrow mb-4">Gifting</div>
            <h2 className="font-serif text-4xl md:text-5xl text-ink leading-[1.05] tracking-tight text-balance">
              Gift a memorable experience,<br /><em className="not-italic text-forest">gift an e-card.</em>
            </h2>
            <p className="text-ink-soft mt-6 text-base md:text-lg leading-relaxed text-pretty">
              Tarzan e-gift cards are the perfect way to make someone's year. Treat them to a luxurious stay at one of our handpicked nature retreats — and watch their face light up.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/contact"
                data-testid="home-giftcard-cta"
                className="inline-flex items-center gap-2 bg-forest text-ivory px-8 py-3.5 rounded-full text-sm font-medium hover:bg-forest-700 transition-all hover:-translate-y-0.5"
              >
                Gift an experience <ArrowRight className="h-4 w-4" />
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 border border-forest/30 text-forest px-8 py-3.5 rounded-full text-sm font-medium hover:bg-forest hover:text-ivory transition-colors"
              >
                Corporate gifting
              </Link>
            </div>
            <div className="grid grid-cols-3 gap-6 mt-10 max-w-md">
              {[
                ["Any amount", "₹5,000 to ₹5,00,000"],
                ["Valid 1 year", "Use across stays"],
                ["Instant delivery", "Personalised note"],
              ].map(([h, b]) => (
                <div key={h}>
                  <div className="text-[10px] uppercase tracking-[0.18em] text-forest font-semibold">{h}</div>
                  <div className="text-xs text-ink-soft mt-1 leading-snug">{b}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ============ GALLERY MARQUEE ============ */}
      <section className="py-16 bg-forest-50/60 overflow-hidden" data-testid="home-marquee">
        <div className="container-tarzan mb-10 flex items-end justify-between">
          <div>
            <div className="eyebrow mb-2">In-Retreat Moments</div>
            <h2 className="font-serif text-3xl md:text-4xl text-ink tracking-tight">A visual journey.</h2>
          </div>
          <Link to="/gallery" className="text-forest text-sm tracking-[0.18em] uppercase font-semibold hover:gap-3 inline-flex items-center gap-2 transition-all">Open Gallery <ArrowRight className="h-4 w-4" /></Link>
        </div>
        <div className="relative">
          <div className="flex gap-5 animate-[marquee_45s_linear_infinite] w-max">
            {[...MARQUEE, ...MARQUEE].map((src, i) => (
              <div key={i} className="w-[260px] md:w-[360px] aspect-[4/3] rounded-2xl overflow-hidden shrink-0 soft-shadow">
                <img src={src} alt="" className="w-full h-full object-cover" loading="lazy" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ NEWSLETTER ============ */}
      <section className="relative py-24 overflow-hidden bg-forest-900 text-ivory">
        <img src={NEWSLETTER_BG} alt="" className="absolute inset-0 w-full h-full object-cover opacity-20" />
        <div className="absolute inset-0 bg-gradient-to-b from-forest-900/90 to-forest-900" />
        <div className="relative container-tarzan text-center max-w-2xl mx-auto">
          <div className="eyebrow text-sand-200 mb-4">Inspired Inbox</div>
          <h2 className="font-serif text-4xl md:text-5xl leading-tight text-balance">Stay inspired.<br/>Get exclusive offers.</h2>
          <p className="text-ivory/70 mt-4 leading-relaxed text-pretty">Subscribe and be the first to know about new retreats, private launches and travel inspiration.</p>
          <form onSubmit={subscribe} className="mt-9 bg-ivory/10 backdrop-blur-md border border-ivory/20 rounded-full p-2 flex items-center max-w-lg mx-auto">
            <input
              type="email"
              required
              value={email}
              onChange={(e)=>setEmail(e.target.value)}
              data-testid={HOME.newsletterEmail}
              placeholder="Enter your email address"
              className="flex-1 bg-transparent outline-none px-5 text-ivory placeholder:text-ivory/50 text-sm"
            />
            <button data-testid={HOME.newsletterSubmit} type="submit" className="bg-forest-400 text-forest-900 px-6 py-3 rounded-full text-sm font-semibold hover:bg-ivory transition-colors">Subscribe</button>
          </form>
        </div>
      </section>
    </div>
  );
}

function SearchField({ icon, label, children, divider = false }) {
  return (
    <label className={`flex-1 flex items-center gap-3 px-5 py-3 rounded-xl md:rounded-full hover:bg-forest-50/60 transition-colors cursor-text min-w-0 ${divider ? "md:border-r md:border-forest/10" : ""}`}>
      <div className="shrink-0">{icon}</div>
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-[0.18em] text-ink-soft font-semibold leading-none">{label}</div>
        <div className="mt-1.5 min-h-[18px]">{children}</div>
      </div>
    </label>
  );
}
